import { InteractionState } from "@/components/organisms/interaction/types";
import {
	Accordion,
	AccordionToggleEventHandler,
	mergeClasses,
	Text,
} from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAllInteractionsStyle } from "./style";
import { useIntl } from "react-intl";
import { ListItemProps } from "@/components/atoms/list";
import { useActiveStyle } from "@/components/organisms/interaction/activeStyle";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import { Flex } from "@/components/atoms/flex";
import { TabHeader } from "@/components/atoms/tabHeader/TabHeader";
import { TabType } from "@/common/stores/persistingStores/navigation";
import {
	InteractionDto,
	InteractionHandlingStatus,
	MonitorTypes,
} from "@/repository/models";
import {
	InteractionTimerType,
	mapInteractionsToListItems,
} from "./interactionMapper";
import { useMessage } from "@/common/messages";
import {
	InteractionListAccordionItem,
	InteractionListAccordionItemProps,
} from "@/components/molecules/listAccordionItem";
import { ItemDetailsDialogMobile } from "@/components/molecules/itemDetailsDialogMobile";
import { ItemProp } from "@/components/atoms/itemDetailsMobile";
import { getInteractionName } from "@/common/helpers";
import { SkillList } from "@/components/atoms/skillList";
import { TagStack } from "@/components/atoms/tagStack";
import { useTagsExist } from "@/common/stores/persistingStores/app/appSettingsStore";
import Timer from "@/components/atoms/timer/Timer";
import { useTransferActions } from "@/components/molecules/allActiveInteractionTable/useTransferAction";
import {
	useWaitingInteractions,
	useWaitingInteractionActions,
	useActiveInteractions,
	useActiveInteractionsActions,
} from "@/hooks/allInteractions";
import {
	InteractionFilterActions,
	useInteractionFilterStore,
} from "@/common/stores/nonpersistingStores/interactionfilter";
import {
	AppCacheActions,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";

export const AllInteractionsMobile = () => {
	const classes = useAllInteractionsStyle();
	const activeClasses = useActiveStyle();
	const interactionClasses = useInteractionStyle();

	const intl = useIntl();
	const [openItems, setOpenItems] = useState([InteractionState.Active]);

	const [isDetailsDialogOpen, setIsDetailsDialogOpen] =
		useState<boolean>(false);

	const { getWhisperButton, getMonitoringActionButton } =
		useActiveInteractionsActions();

	const [selectedInteraction, setSelectedInteraction] =
		useState<InteractionDto>();

	const [selectedInteractionHandlingType, setSelectedInteractionHandlingType] =
		useState<InteractionHandlingStatus>();

	const [itemDetailsList, setItemDetailsList] = useState<ItemProp[]>();

	const { show: showMessage } = useMessage();

	const toggleInteractionCategoryVisibility: AccordionToggleEventHandler<
		InteractionState
	> = (_, data) => {
		setOpenItems(data.openItems);
	};

	const { getTextColdTransferButtons } = useTransferActions();

	const { activeInteractions } = useActiveInteractions();

	const { nonVoicemailInteractions, voicemailInteractions } =
		useWaitingInteractions();

	const { getWaitingItemActions } = useWaitingInteractionActions();

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const filterDataActions = useInteractionFilterStore(
		(actions: InteractionFilterActions) => actions
	);

	useEffect(() => {
		filterDataActions.setActive({ queueIds: [], types: [] });
		appStoreActions.removeCaches([CacheKey.ActiveListFilterData]);

		filterDataActions.setWaiting({ queueIds: [], types: [] });
		appStoreActions.removeCaches([CacheKey.WaitingListFilterData]);
	}, []);

	const tagsExist = useTagsExist();

	const createDetail = (
		labelId: string,
		mainValue: any,
		secondaryValue?: any
	) => ({
		label: intl.formatMessage({ id: `Interaction.Table.${labelId}` }),
		mainValue,
		secondaryValue,
	});

	const openInteractionDetailsDialog = useCallback(
		(i: InteractionDto, status: InteractionHandlingStatus) => {
			setItemDetailsList([
				createDetail(
					"Customer",
					getInteractionName(i.type, i.name, intl),
					!!i.contactInformation && i.contactInformation !== i.name
						? i.contactInformation
						: undefined
				),
				createDetail("Queue", i.queueName),
				createDetail(
					"Skills",
					i.skills?.length ? <SkillList skillList={i.skills} /> : "-"
				),
				// Dropped entirely rather than shown as a dash when the tenant has no tag configured, so
				// mobile matches the desktop table, which excludes the column outright.
				...(tagsExist
					? [createDetail("Tags", <TagStack tags={i.assignedTags} />)]
					: []),
				createDetail("Agent", i.agentName),
				createDetail("Timer", <Timer startDate={i?.startDate} />),
			]);

			setSelectedInteraction(i);
			setSelectedInteractionHandlingType(status);

			setIsDetailsDialogOpen(true);
		},
		[
			getWhisperButton,
			getMonitoringActionButton,
			getTextColdTransferButtons,
			tagsExist,
		]
	);

	const getSelectedItemActions = useCallback(() => {
		if (!selectedInteraction || !isDetailsDialogOpen) {
			return [];
		}

		let actions = [];
		if (selectedInteractionHandlingType == InteractionHandlingStatus.Active) {
			actions = [
				getWhisperButton(selectedInteraction),
				getMonitoringActionButton(selectedInteraction, MonitorTypes.Silent),
				getMonitoringActionButton(selectedInteraction, MonitorTypes.BargeIn),
				getTextColdTransferButtons(selectedInteraction),
			];
		} else {
			actions = [getWaitingItemActions(selectedInteraction)];
		}
		return actions.filter(Boolean);
	}, [
		getTextColdTransferButtons,
		getMonitoringActionButton,
		getWhisperButton,
		selectedInteraction,
		selectedInteractionHandlingType,
		isDetailsDialogOpen,
		getWaitingItemActions,
	]);

	const accordionActivePanelView = useMemo<ListItemProps[]>(() => {
		return mapInteractionsToListItems(activeInteractions, {
			intl,
			interactionHandlingStatus: InteractionHandlingStatus.Active,
			timerType: InteractionTimerType.LiveTimer,
			onItemClick: (interaction) =>
				openInteractionDetailsDialog(
					interaction,
					InteractionHandlingStatus.Active
				),
		});
	}, [
		activeInteractions,
		intl,
		getTextColdTransferButtons,
		getMonitoringActionButton,
		getWhisperButton,
	]);

	useEffect(() => {
		if (
			selectedInteraction &&
			activeInteractions?.findIndex((x) => x.id == selectedInteraction.id) == -1
		) {
			setIsDetailsDialogOpen(false);
			setSelectedInteraction(undefined);

			showMessage("allInteractions", {
				intent: "info",
				text: intl.formatMessage({
					id: "MobileView.AllInteractions.InteractionClosed",
				}),
			});
		}
	}, [activeInteractions]);

	const accordionWaitingPanelView = useMemo<ListItemProps[]>(() => {
		return mapInteractionsToListItems(nonVoicemailInteractions, {
			intl,
			interactionHandlingStatus: InteractionHandlingStatus.Waiting,
			timerType: InteractionTimerType.LiveTimer,
			onItemClick: (interaction) => {
				openInteractionDetailsDialog(
					interaction,
					InteractionHandlingStatus.Waiting
				);
			},
		});
	}, [nonVoicemailInteractions, intl]);

	const accordionVoicemailPanelView = useMemo<ListItemProps[]>(() => {
		return mapInteractionsToListItems(voicemailInteractions, {
			intl,
			interactionHandlingStatus: InteractionHandlingStatus.Waiting,
			timerType: InteractionTimerType.FormattedDate,
			onItemClick: (interaction) => {
				openInteractionDetailsDialog(
					interaction,
					InteractionHandlingStatus.Waiting
				);
			},
		});
	}, [voicemailInteractions, intl]);

	const accordionConfigs: InteractionListAccordionItemProps[] = useMemo(
		() => [
			{
				value: InteractionState.Active,
				items: accordionActivePanelView,
				listClassNames: {
					item: activeClasses.item,
					itemSelected: activeClasses.itemSelected,
					startMedia: interactionClasses.iconCircle,
					headerMedia: mergeClasses(
						interactionClasses.listHeaderMedia,
						classes.listHeaderMedia
					),
				},
			},
			{
				value: InteractionState.Waiting,
				items: accordionWaitingPanelView,
				listClassNames: {
					item: activeClasses.item,
					itemSelected: activeClasses.itemSelected,
					startMedia: interactionClasses.iconCircle,
					headerMedia: mergeClasses(
						interactionClasses.listHeaderMedia,
						classes.listHeaderMedia
					),
				},
			},
			{
				value: InteractionState.Voicemail,
				items: accordionVoicemailPanelView,
				listClassNames: {
					item: activeClasses.item,
					itemSelected: activeClasses.itemSelected,
					startMedia: interactionClasses.iconCircle,
					headerMedia: interactionClasses.listHeaderMedia,
				},
			},
		],
		[
			accordionActivePanelView,
			accordionWaitingPanelView,
			accordionVoicemailPanelView,
			activeClasses,
			interactionClasses,
			classes,
		]
	);

	const accordionItems = useMemo(
		() => accordionConfigs.map((x) => <InteractionListAccordionItem {...x} />),
		[accordionConfigs, classes]
	);

	return (
		<div style={{ overflow: "scroll", height: "100%" }}>
			<TabHeader tab={TabType.SupervisorInteractions} />
			<Flex column>
				<Text></Text>
			</Flex>
			<Accordion
				multiple
				collapsible
				openItems={openItems}
				onToggle={toggleInteractionCategoryVisibility}
			>
				{accordionItems}
			</Accordion>
			<ItemDetailsDialogMobile
				itemProps={itemDetailsList}
				itemActions={getSelectedItemActions()}
				titleKey={"MobileView.InteractionDetails"}
				open={isDetailsDialogOpen}
				setOpen={setIsDetailsDialogOpen}
			/>
		</div>
	);
};
